import { serviceOrigin } from '../lib/services';
import { instanceReady } from './useInstanceMode';
import { useCallback, useEffect, useRef, useState } from 'react';
import { onAuthExpired } from '../api/client';

// 14. Registry-Fund (Cloud-Befund 17.08., Davids Instanz): Der GESAMTE Login
// (login/me/refresh/google/logout) ging hart gegen die Betreiber-auth —
// unabhängig von Registry und Instanz-Map. Deshalb war Alex' Betreiber-Konto
// auf Davids Kundeninstanz gültig. Jetzt: Ursprung aus der Registry, und jeder
// Auth-Call wartet die Instanz-Map ab (instanceReady), damit auch der erste
// Aufruf gegen die richtige auth geht.
async function authBase(): Promise<string> {
  await instanceReady();
  const o = serviceOrigin('auth');
  if (!o) throw new Error('auth_service_unavailable');
  return o;
}
const STORAGE_KEY = 'ark-auth';
const AUTH_DISABLED = import.meta.env.VITE_DISABLE_AUTH === 'true';

// Email gating is server-side now: /auth/me 200 = user is in the DB and
// active → allowed. /auth/me non-2xx = rejected. No client-side whitelist
// to maintain. New users created via AdminUsers can sign in immediately
// without a frontend redeploy.

export const ADMIN_EMAILS = [
  'apopovic.aut@gmail.com',
  'alex@arkturian.com',
  'wow.wunderwelten@gmail.com',
  'local@cloud',
];

// Private host gating: cloud.mac / cloud2.mac are Alex's local Mac instances
// and must NEVER be exposed to customers (their bots and tenants live on
// arkturian / arkserver / oneal / pdrei). ADMIN_EMAILS would let Sabrina
// in too — we don't want that here. PRIVATE_HOST_ALLOWED is the strict
// set of operator emails for these Mac frontends only.
const PRIVATE_HOSTS = new Set([
  'cloud.mac.arkturian.com',
  'cloud2.mac.arkturian.com',
]);
const PRIVATE_HOST_ALLOWED = new Set([
  'apopovic.aut@gmail.com',
  'alex@arkturian.com',
  't.saier@edera-safety.com',
  'jascha.popovic@bellevue-living.net', // Jascha — pdrei node operator (Alex 2026-07-02)
]);

export function checkPrivateHost(email: string): { ok: boolean; reason?: string } {
  const host = (typeof window !== 'undefined' ? window.location.host : '');
  if (!PRIVATE_HOSTS.has(host)) return { ok: true };
  if (!PRIVATE_HOST_ALLOWED.has(email)) {
    return {
      ok: false,
      reason: `Diese Instanz (${host}) ist privat. Bitte verwende cloud.arkturian.com, cloud.arkserver.arkturian.com, cloud-api.oneal.arkturian.com oder cloud.pdrei.arkturian.com.`,
    };
  }
  return { ok: true };
}

export interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_superadmin: boolean;
  // RBAC fields from /auth/me
  agentos_role?: string;
  allowed_agents?: string[] | null;
  max_agents?: number;
  // Multi-email canonical users — every alias here is treated as
  // 'me' for owner_email matching in filters / sessions list.
  // Empty array for legacy users without aliases.
  aliases?: string[];
  // Realtime voice access control (#1220, auth-api Voice-Lane). Only show the
  // voice toggle when realtime_enabled && realtime_endpoint; mint/usage/devlog
  // go to realtime_endpoint (server-resolved per tenant — Alex→arkturian,
  // Jascha→pdrei/P3-key). NEVER hardcode the endpoint.
  realtime_enabled?: boolean;
  realtime_profile_id?: string | null;
  realtime_endpoint?: string | null;
}

const STUB_USER: AuthUser = {
  id: 'local',
  email: 'local@cloud',
  first_name: 'Local',
  last_name: 'User',
  is_superadmin: true,
  agentos_role: 'admin',
  allowed_agents: null,
  max_agents: 100,
  // Local dev (AUTH_DISABLED): allow voice against the default host so the
  // toggle is testable without a real /auth/me.
  realtime_enabled: true,
  realtime_profile_id: 'alex-default',
  realtime_endpoint: 'https://api-ai.arkturian.com',
};

interface StoredAuth {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

function loadStored(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveTokens(accessToken: string, refreshToken: string, expiresIn: number) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      access_token: accessToken,
      refresh_token: refreshToken,
      expires_at: Date.now() + expiresIn * 1000,
    })
  );
}

function clearStored() {
  localStorage.removeItem(STORAGE_KEY);
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(AUTH_DISABLED ? STUB_USER : null);
  const [loading, setLoading] = useState(!AUTH_DISABLED);
  const [error, setError] = useState('');
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>(null);

  const scheduleRefresh = useCallback((expiresAt: number) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const delay = Math.max(0, expiresAt - Date.now() - 60_000);
    refreshTimer.current = setTimeout(async () => {
      const stored = loadStored();
      if (!stored) return;
      try {
        const res = await fetch(`${await authBase()}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: stored.refresh_token }),
        });
        if (res.ok) {
          const data = await res.json();
          saveTokens(data.access_token, data.refresh_token, data.expires_in);
          scheduleRefresh(Date.now() + data.expires_in * 1000);
          return;
        }
        // Only a DEFINITIVE auth rejection ends the session (refresh_token
        // genuinely invalid/expired). A transient proxy hiccup (5xx) must NOT
        // — otherwise a backend blip silently logs the operator out and every
        // downstream call (issue-api etc.) starts 401ing. Retry instead.
        if (res.status === 401 || res.status === 403 || res.status === 400) {
          clearStored();
          setUser(null);
          return;
        }
        scheduleRefresh(Date.now() + 75_000); // transient 5xx → retry in ~15s
      } catch {
        // NETWORK flap (net::ERR_NETWORK_CHANGED / offline) at the refresh
        // moment. A dropped connection must NOT nuke the session — the token
        // may still be valid, we just couldn't reach auth-api right now. Keep
        // the stored tokens and retry in ~15s; self-heals once the link is
        // back, no reload needed. (Alex's Mama-WiFi, 2026-07-01.)
        scheduleRefresh(Date.now() + 75_000);
      }
    }, delay);
  }, []);

  const fetchMe = useCallback(async (accessToken: string): Promise<AuthUser | null> => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(`${await authBase()}/api/v1/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) return null;
      const u: AuthUser = await res.json();
      // Backend gates the user list; we only enforce the private-host
      // restriction (cloud.mac / cloud2.mac are operator-only).
      if (!checkPrivateHost(u.email).ok) {
        clearStored();
        return null;
      }
      return u;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (AUTH_DISABLED) return;

    // Handle OAuth callback
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (window.location.pathname === '/auth/google/callback' && code) {
      setLoading(true);
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      authBase().then((b) => fetch(`${b}/api/v1/auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: redirectUri }),
      }))
        .then(async (res) => {
          if (!res.ok) throw new Error('Login failed');
          const data = await res.json();
          // Backend gates by user-DB membership (auth-api returns user obj
          // only if email is present + active). We only re-check the
          // private-host restriction for cloud.mac / cloud2.mac.
          const hostCheck = checkPrivateHost(data.user?.email || '');
          if (!hostCheck.ok) {
            setError(hostCheck.reason || 'Zugriff verweigert');
            return;
          }
          saveTokens(data.access_token, data.refresh_token, data.expires_in);
          setUser(data.user);
          scheduleRefresh(Date.now() + data.expires_in * 1000);
        })
        .catch(() => setError('Google Login fehlgeschlagen'))
        .finally(() => {
          window.history.replaceState({}, '', '/');
          setLoading(false);
        });
      return;
    }

    // Restore session
    const stored = loadStored();
    if (!stored) {
      setLoading(false);
      return;
    }

    if (stored.expires_at > Date.now()) {
      fetchMe(stored.access_token).then((u) => {
        if (u) {
          setUser(u);
          scheduleRefresh(stored.expires_at);
        } else {
          clearStored();
        }
        setLoading(false);
      });
    } else {
      authBase().then((b) => fetch(`${b}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: stored.refresh_token }),
      }))
        .then(async (res) => {
          if (!res.ok) throw new Error();
          const data = await res.json();
          saveTokens(data.access_token, data.refresh_token, data.expires_in);
          const u = await fetchMe(data.access_token);
          if (u) {
            setUser(u);
            scheduleRefresh(Date.now() + data.expires_in * 1000);
          } else {
            clearStored();
          }
        })
        .catch(() => clearStored())
        .finally(() => setLoading(false));
    }

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [fetchMe, scheduleRefresh]);

  // Email + password login. Mirrors the google callback handler:
  // hits POST /auth/login → saves tokens → re-checks private-host gate
  // → setUser. Throws on bad creds / network so the form can show the
  // error inline instead of via the shared `error` state.
  const loginWithEmail = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${await authBase()}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      let detail = '';
      try { detail = (await res.json())?.detail || ''; } catch { /* */ }
      throw new Error(detail || `Login fehlgeschlagen (HTTP ${res.status})`);
    }
    const data = await res.json();
    const hostCheck = checkPrivateHost(data.user?.email || '');
    if (!hostCheck.ok) throw new Error(hostCheck.reason || 'Zugriff verweigert');
    saveTokens(data.access_token, data.refresh_token, data.expires_in);
    setUser(data.user);
    scheduleRefresh(Date.now() + data.expires_in * 1000);
  }, [scheduleRefresh]);

  const startGoogleLogin = useCallback(async () => {
    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const res = await fetch(
        `${await authBase()}/api/v1/auth/google/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`
      );
      if (!res.ok) {
        setError('Google Login nicht verfügbar');
        return;
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch {
      setError('Verbindung fehlgeschlagen');
    }
  }, []);

  // Abgelaufene Sitzung, die sich nicht mehr erneuern lässt: Anmeldung abräumen,
  // damit der Anmeldebildschirm erscheint — statt dass jede einzelne Aktion mit
  // einem technischen 401-Text scheitert (Tommy, 19.08.).
  useEffect(() => onAuthExpired(() => {
    if (AUTH_DISABLED) return;
    clearStored();
    setUser(null);
    setError('Deine Anmeldung ist abgelaufen — bitte melde dich neu an.');
  }), []);

  const logout = useCallback(() => {
    if (AUTH_DISABLED) return;
    const stored = loadStored();
    if (stored) {
      authBase().then((b) => fetch(`${b}/api/v1/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: stored.refresh_token }),
      })).catch(() => {});
    }
    clearStored();
    setUser(null);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  }, []);

  return { user, loading, error, logout, startGoogleLogin, loginWithEmail };
}
